import Button from "@/common/components/Button";
import { FileUploader } from "@/common/components/FileUploader";
import MaskedTextField from "@/common/components/MaskedTextField";
import Modal from "@/common/components/Modal";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { useId, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { addFiscalDocumentSchema, AddFiscalDocumentValidationSchema } from "../../schemas/addFiscalDocument.schema";
import { useCreateBillFiscalDocument } from "../../hooks/useCreateFiscalDocument";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useQueryClient } from "@tanstack/react-query";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ErrorMessage } from "@/common/components/ErrorMessage";

interface AddFiscalDocumentModalProps {
  billId: string;
}

const AddFiscalDocumentModal = ({ billId }: AddFiscalDocumentModalProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [isError, setIsError] = useState<boolean>(false);
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [pdfFile, setPdfFile] = useState<File>();
  const [xmlFile, setXmlFile] = useState<File>();

  const { mutate: createBillFiscalDocument } = useCreateBillFiscalDocument({
    billId,
    onSuccess() {
      resetForm();
      setIsOpen(false);
      setIsError(false);
      setMessages(['Nota fiscal adicionada com sucesso!']);
    },
    onError(error) {
      FormTransformers.errorsTransformer({ setError })(error);

      const { errors } = error?.response?.data as { errors: Record<string, string[]> };

      if (errors.base) {
        setIsError(true);
        setMessages(errors.base);
      }
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<AddFiscalDocumentValidationSchema>({
    resolver: zodResolver(addFiscalDocumentSchema),
    defaultValues: {
      number: '',
      serial: '',
    }
  });

  const onSubmit: SubmitHandler<AddFiscalDocumentValidationSchema> = (form) => createBillFiscalDocument({
    form,
    pdf: pdfFile,
    xml: xmlFile,
  });

  const resetForm = () => {
    reset();
    setPdfFile(undefined);
    setXmlFile(undefined);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={(value) => {
          if (!value) {
            resetForm();
          }

          setIsOpen(value);
        }}
        Trigger={(
          <Button>
            Adicionar NF
          </Button>
        )}
        title="Adicionar NF"
        footer="Cadastrar NF"
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <form className="flex flex-col gap-4">
            <TextField
              label="Número da NF"
              maxLength={64}
              markAsRequired
              {...register('number')}
              errorMessage={errors.number?.message || undefined}
            />

            <MaskedTextField
              mask={''.padEnd(8, '0')}
              label="Número serial"
              markAsRequired
              {...register('serial')}
              errorMessage={errors.serial?.message || undefined}
            />

            <div className="flex flex-col gap-1">
              <FileUploader
                id={`fiscal-document-pdf-file-${useId()}`}
                title="Anexe o PDF da nota fiscal"
                className="w-full bg-yellow-op-2 text-white-400 border-none rounded-lg"
                accept="application/pdf"
                fileName={pdfFile?.name}
                currentFileUrl={pdfFile ? URL.createObjectURL(pdfFile) : undefined}
                handleChange={setPdfFile}
              />

              {errors.pdf?.message && <ErrorMessage name='pdf' message={errors.pdf?.message} />}
            </div>

            <div className="flex flex-col gap-1">
              <FileUploader
                id={`fiscal-document-xml-file-${useId()}`}
                title="Anexe o XML da nota fiscal"
                className="w-full bg-yellow-op-2 text-white-400 border-none rounded-lg"
                accept="text/xml"
                fileName={xmlFile?.name}
                currentFileUrl={xmlFile ? URL.createObjectURL(xmlFile) : undefined}
                handleChange={setXmlFile}
              />

              {errors.xml?.message && <ErrorMessage name='pdf' message={errors.xml?.message} />}
            </div>
          </form>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
          onClose={() => {
            queryClient.invalidateQueries({ queryKey: ['billingBillFiscalDocuments', billId] });
          }}
        />
      )}
    </>
  );
};

export default AddFiscalDocumentModal;
